/**
 * Form Types — Shared contract re-exports + app-local types
 *
 * Task: T-P5-D4-F2-1 (Shared Form Contract Upgrade)
 *
 * Field-level schemas and types now live in @telify/shared.
 * This file re-exports them for backward compatibility, and keeps
 * app-local types (FormDefinition, error DTOs, etc.) that are
 * specific to the webapp booking flow.
 */

import { z } from 'zod';
import { formFieldSchema } from '@telify/shared';

// ============================================
// Re-exports from @telify/shared (backward compat)
// ============================================

export {
  fieldTypeEnum,
  formOptionSchema,
  formFieldSchema,
  formResponseInputSchema,
  showWhenSchema,
  tagRuleSchema,
  SHOW_WHEN_OPERATORS,
  type FieldType,
  type FormOption,
  type FormField,
  type FormResponseInput,
  type ShowWhen,
  type ShowWhenOperator,
  type TagRule,
} from '@telify/shared';

// ============================================
// Form Definition Schema (app-local)
// ============================================

/**
 * Form definition as returned from GET /api/webapp/forms/:formId
 *
 * Response behavior:
 * - If NOT yet submitted: includes `savedData` with partial responses (if any)
 * - If IS already submitted: includes `submission` marker, NO savedData
 */
export const formDefinitionSchema = z.object({
  formId: z.string(),
  formType: z.enum(['REGISTRATION', 'INTAKE', 'WAIVER', 'GENERAL']),
  title: z.string(),
  description: z.string().optional(),
  fields: z.array(formFieldSchema),
  savedData: z.record(z.unknown()).optional(),
  submission: z
    .object({
      submissionId: z.string(),
      submittedAt: z.string(),
    })
    .optional(),
  // registration-privacy-consent: the parse strips unlisted keys, so the flag must be named here.
  privacyConsentRequired: z.boolean().optional(),
});

export type FormType = 'REGISTRATION' | 'INTAKE' | 'WAIVER' | 'GENERAL';

/**
 * Form definition type.
 *
 * Uses the shared FormField type from @telify/shared.
 */
export interface FormDefinition {
  formId: string;
  formType: FormType;
  title: string;
  description?: string;
  fields: import('@telify/shared').FormField[];
  savedData?: Record<string, unknown>;
  /** Registration only: the client must tick the privacy box (registration-privacy-consent). */
  privacyConsentRequired?: boolean;
  submission?: {
    submissionId: string;
    submittedAt: string;
  };
}

// ============================================
// Form Response Types (app-local)
// ============================================

/**
 * Success response from POST /api/webapp/forms/:formId/submit (HTTP 201)
 *
 * Note: No `success` field - discriminated by HTTP status.
 * nextStep values:
 * - CUSTOM_FORMS: More forms to complete
 * - PAYMENT: Proceed to payment step
 * - REVIEW: Proceed to review/confirmation step
 */
export interface FormSubmissionSuccess {
  submissionId: string;
  submittedAt: string;
  nextStep: 'CUSTOM_FORMS' | 'PAYMENT' | 'REVIEW';
}

/**
 * Success response from POST /api/webapp/forms/:formId/save (HTTP 200)
 */
export interface FormSaveResult {
  saved: boolean;
  savedAt: string;
  fieldsCount: number;
}

// ============================================
// Error Response Types (app-local)
// ============================================

/**
 * Field-level validation error
 */
export interface FieldError {
  fieldId: string;
  error: string;
}

/**
 * Validation error response (HTTP 400, error code: VALIDATION_FAILED)
 */
export interface FormValidationErrorResponse {
  error: 'VALIDATION_FAILED';
  message: string;
  fieldErrors: FieldError[];
}

/**
 * Already submitted error response (HTTP 409, error code: ALREADY_SUBMITTED)
 */
export interface FormAlreadySubmittedResponse {
  error: 'ALREADY_SUBMITTED';
  message: string;
  submissionId: string;
  submittedAt: string;
}

/**
 * Registration partial save not allowed (HTTP 400)
 * Backend does not support partial saves for registration forms.
 */
export interface RegistrationPartialSaveError {
  error: 'REGISTRATION_PARTIAL_SAVE_NOT_ALLOWED';
  message: string;
}
